import { io, Socket } from 'socket.io-client';
import { storage } from '../utils/storage';
import type { Order, TruckStatus } from '../types';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3001';

let socket: Socket | null = null;

export interface SocketEvents {
  'order:created': (order: Order) => void;
  'order:updated': (order: Order) => void;
  'truck:status': (status: TruckStatus) => void;
  'menu:updated': (data: { timestamp: number }) => void;
  'notification:push': (notification: { title: string; body: string }) => void;
  pong: (data: { timestamp: number }) => void;
}

export const initSocket = (): Socket => {
  if (socket?.connected) {
    return socket;
  }

  const token = storage.getAccessToken();

  socket = io(WS_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket.on('connect', () => {
    console.log('WebSocket connected');
  });

  socket.on('disconnect', (reason) => {
    console.log('WebSocket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('WebSocket connection error:', error);
  });

  return socket;
};

export const getSocket = (): Socket | null => {
  return socket;
};

export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

export const subscribeToOrders = (): void => {
  if (socket) {
    socket.emit('subscribe:orders');
  }
};

export const subscribeToTruck = (): void => {
  if (socket) {
    socket.emit('subscribe:truck');
  }
};

export const onOrderCreated = (callback: SocketEvents['order:created']): void => {
  if (socket) {
    socket.on('order:created', callback);
  }
};

export const onOrderUpdated = (callback: SocketEvents['order:updated']): void => {
  if (socket) {
    socket.on('order:updated', callback);
  }
};

export const onTruckStatus = (callback: SocketEvents['truck:status']): void => {
  if (socket) {
    socket.on('truck:status', callback);
  }
};

export const offOrderCreated = (callback?: SocketEvents['order:created']): void => {
  if (socket) {
    socket.off('order:created', callback);
  }
};

export const offOrderUpdated = (callback?: SocketEvents['order:updated']): void => {
  if (socket) {
    socket.off('order:updated', callback);
  }
};

export const offTruckStatus = (callback?: SocketEvents['truck:status']): void => {
  if (socket) {
    socket.off('truck:status', callback);
  }
};

export const onMenuUpdated = (callback: SocketEvents['menu:updated']): void => {
  if (socket) {
    socket.on('menu:updated', callback);
  }
};

export const offMenuUpdated = (callback?: SocketEvents['menu:updated']): void => {
  if (socket) {
    socket.off('menu:updated', callback);
  }
};

// Reconnect with new token (after login)
export const reconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
  }
  initSocket();
};

export default {
  init: initSocket,
  get: getSocket,
  disconnect: disconnectSocket,
  reconnect: reconnectSocket,
  subscribeToOrders,
  subscribeToTruck,
  onOrderCreated,
  onOrderUpdated,
  onTruckStatus,
  onMenuUpdated,
  offOrderCreated,
  offOrderUpdated,
  offTruckStatus,
  offMenuUpdated,
};
